import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow, format } from 'date-fns';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiMail, FiPhone, FiDollarSign, FiCalendar, FiUser, FiMessageSquare, FiSend } = FiIcons;

const ClientDetails = ({ client, onClose }) => {
  const { tasks, teamMembers } = useData();
  const [activeTab, setActiveTab] = useState('details');
  
  const assignedMember = teamMembers.find(m => m.id === client.assignedTo);
  const clientTasks = tasks.filter(task => task.clientId === client.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{client.name}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Left Panel - Client Details */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span>{client.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiPhone} className="w-4 h-4" />
                      <span>{client.phone}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Business Details</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                      <span>${client.value?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        client.stage === 'active' ? 'bg-green-100 text-green-800' :
                        client.stage === 'negotiation' ? 'bg-purple-100 text-purple-800' :
                        client.stage === 'proposal' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {client.stage}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Assigned To</h4>
                {assignedMember ? (
                  <div className="flex items-center space-x-3">
                    <img
                      src={assignedMember.avatar}
                      alt={assignedMember.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{assignedMember.name}</p>
                      <p className="text-sm text-gray-600">{assignedMember.department}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Not assigned</p>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Timeline</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>Created {formatDistanceToNow(new Date(client.createdAt), { addSuffix: true })}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>Last contact {formatDistanceToNow(new Date(client.lastContact), { addSuffix: true })}</span>
                  </div>
                </div>
              </div>

              {client.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                  <p className="text-gray-600">{client.notes}</p>
                </div>
              )}

              {clientTasks.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Related Tasks</h4>
                  <div className="space-y-2">
                    {clientTasks.map(task => (
                      <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <h5 className="font-medium text-gray-900">{task.title}</h5>
                          <p className="text-sm text-gray-600">{task.description}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          task.status === 'completed' ? 'bg-green-100 text-green-800' :
                          task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Activity Feed */}
          <div className="w-80 border-l border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">Activity Feed</h4>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                <div className="flex space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      Client was created
                    </p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(client.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiMessageSquare} className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      Last contact made
                    </p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(client.lastContact), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Add a note..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientDetails;